import dayjs from "dayjs";
import { UserComment } from "~/store/partnerUserComments";

type Rating = {
  value: number;
  count: number;
  userComments: UserComment[];
};

type Address = {
  city: string;
  postalCode: string;
  street: string;
  streetNumber: string;
  geometry: {
    lat: number;
    lng: number;
  };
};

type Organization = {
  name: string;
  urlPath: string;
  logoImageUrl: string;
};

type Company = Organization & {
  rating: Rating;
};

type Agency = Organization & {
  address: Address;
  contact?: {
    email: string;
    telephone: string;
  };
  rating: Rating;
};

export function generateBreadcrumbJsonLd(
  data: { name: string; linkUrl: string }[],
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: {
          name: "Startseite",
          "@id": "https://www.esyoil.com",
        },
      },
      ...data.map((crumb, i) => ({
        "@type": "ListItem",
        position: i + 2,
        item: {
          name: crumb.name,
          "@id": `https://www.esyoil.com${crumb.linkUrl}`,
        },
      })),
    ],
  };
}

export function generateCompanyJsonLd({
  name,
  urlPath,
  logoImageUrl,
  rating,
}: Company) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: name,
    url: `https://www.esyoil.com${urlPath}`,
    logo: logoImageUrl,
    ...generateRatingAndReviewJsonLd(rating),
  };
}

export function generateAgencyJsonLd({
  name,
  urlPath,
  logoImageUrl,
  address,
  contact,
  rating,
}: Agency) {
  const contactJson = contact && {
    email: contact.email.toLocaleLowerCase(),
    telephone: contact.telephone,
  };

  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: name,
    url: `https://www.esyoil.com${urlPath}`,
    logo: logoImageUrl,
    currenciesAccepted: "EUR",
    openingHoursSpecification: [],
    address: {
      "@type": "PostalAddress",
      addressCountry: "DE",
      addressLocality: address.city,
      postalCode: address.postalCode,
      streetAddress: `${address.street} ${address.streetNumber}`,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: address.geometry.lat,
      longitude: address.geometry.lng,
    },
    ...contactJson,
    ...generateRatingAndReviewJsonLd(rating),
  };
}

function generateRatingAndReviewJsonLd({ count, value, userComments }: Rating) {
  const aggregateRating =
    count > 0
      ? {
          "@type": "AggregateRating",
          ratingValue: value,
          ratingCount: count,
        }
      : undefined;

  const review = userComments.length
    ? userComments.map((comment) => ({
        "@type": "Review",
        author: {
          "@type": "Person",
          name: "esyoil Kunde",
        },
        datePublished: dayjs(comment.updatedAtByCustomer).format("YYYY-MM-DD"),
        reviewBody: comment.customerStatement,
        reviewRating: {
          "@type": "Rating",
          ratingValue: comment.rating,
          bestRating: 5,
          worstRating: 1,
        },
      }))
    : undefined;

  return {
    aggregateRating,
    review,
  };
}
